import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { del, get, post } from '../api';
import { useMe } from '../auth';
import { Alert, Card, CodeBlock, Empty, fmtRelative, PageHeader, SecretBox, Spinner, StatusBadge, useAction, useLoader } from '../components/ui';
import type { ApiKey, ApiKeyCreated } from '../types';

function Integration({ sampleKey }: { sampleKey: string }) {
  const origin = window.location.origin;
  const wsOrigin = origin.replace(/^http/, 'ws');
  const curl = `curl -X POST ${origin}/v1/messages \\
  -H "Authorization: Bearer ${sampleKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message_id": "msg_123",
    "user_id": "user_42",
    "content": "Hey, check out this offer!",
    "conversation_id": "room_7",
    "user_name": "Jane",
    "timestamp": "${new Date().toISOString().replace(/\.\d+Z$/, 'Z')}",
    "metadata": { "channel": "chat" },
    "previous_messages": [
      { "message_id": "msg_122", "user_id": "user_42", "content": "hi all", "timestamp": 1735689600 }
    ]
  }'`;
  const ws = `${wsOrigin}/v1/stream?api_key=${sampleKey}`;
  const wsExample = `const ws = new WebSocket("${ws}");
ws.onopen = () => ws.send(JSON.stringify({
  message_id: "msg_124", user_id: "user_42", content: "hello", conversation_id: "room_7"
}));
// Frames received: {"type":"ack",...}, {"type":"decision","data":{...}}, {"type":"error",...}
ws.onmessage = (e) => console.log(JSON.parse(e.data));`;
  const verify = `import crypto from "node:crypto";

// header: x-msgguard-signature: t=<unix seconds>,v1=<hex hmac-sha256>
function verify(rawBody, header, secret, toleranceSec = 300) {
  const parts = Object.fromEntries(header.split(",").map((p) => p.split("=")));
  const t = Number(parts.t);
  if (!t || !parts.v1 || Math.abs(Date.now() / 1000 - t) > toleranceSec) return false;
  const expected = crypto.createHmac("sha256", secret).update(\`\${t}.\${rawBody}\`).digest();
  const given = Buffer.from(parts.v1, "hex");
  return given.length === expected.length && crypto.timingSafeEqual(given, expected);
}`;

  return (
    <Card title="Integration" subtitle="Send messages over REST or WebSocket and receive decisions by webhook.">
      <div className="docs">
        <h3>1. Submit a message</h3>
        <p>
          <code>POST /v1/messages</code> returns <code>202</code> with <code>{'{ id, message_id, status: "queued" }'}</code>. Re-sending
          the same <code>message_id</code> is idempotent (<code>200</code>, <code>duplicate: true</code>). Required fields are{' '}
          <code>message_id</code>, <code>user_id</code> and <code>content</code>; <code>timestamp</code> accepts ISO-8601 or Unix
          seconds/milliseconds. Authenticate with <code>Authorization: Bearer</code> or <code>x-api-key</code>. Up to 100 messages
          can be sent at once to <code>POST /v1/messages/batch</code> as <code>{'{ "messages": [...] }'}</code>.
        </p>
        <CodeBlock label="curl" code={curl} />

        <h3>2. Or stream over WebSocket</h3>
        <p>
          Send one JSON message per frame. Add <code>&amp;decisions=false</code> to receive only acknowledgements.
        </p>
        <CodeBlock label="WebSocket URL" code={ws} />
        <CodeBlock label="JavaScript" code={wsExample} />

        <h3>3. Receive decisions by webhook</h3>
        <p>
          Configure the endpoint in <Link to="/settings/webhook">Webhook settings</Link>. Each request carries{' '}
          <code>x-msgguard-signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code>, where <code>v1</code> is the HMAC-SHA256 of{' '}
          <code>"&lt;t&gt;.&lt;raw body&gt;"</code> keyed with your webhook signing secret. Verify it against the raw request body
          before parsing, and reject stale timestamps. You can also poll <code>GET /v1/messages/:message_id</code> and{' '}
          <code>GET /v1/users/:user_id</code>.
        </p>
        <CodeBlock label="Node.js verification" code={verify} />
      </div>
    </Card>
  );
}

export function ApiKeysPage() {
  const { isAdmin } = useMe();
  const { data, error, loading, reload } = useLoader(
    () => (isAdmin ? get<{ api_keys: ApiKey[] }>('/api/api-keys') : Promise.resolve({ api_keys: [] as ApiKey[] })),
    [isAdmin],
  );
  const act = useAction();
  const [name, setName] = useState('');
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const key = await act.run('create', () => post<ApiKeyCreated>('/api/api-keys', { name: name.trim() }));
    if (key) {
      setCreated(key);
      setName('');
      void reload(true);
    }
  };
  const revoke = (k: ApiKey) => {
    if (!window.confirm(`Revoke "${k.name}" (${k.prefix}…)? Integrations using it will stop working immediately.`)) return;
    void act.run(`revoke-${k.id}`, async () => {
      await del(`/api/api-keys/${k.id}`);
      await reload(true);
    });
  };

  const keys = data?.api_keys ?? [];

  return (
    <>
      <PageHeader title="API keys" subtitle="Keys authenticate your servers against the ingestion API (/v1)." />
      {!isAdmin ? (
        <Alert kind="info">Only admins and owners can view and manage API keys.</Alert>
      ) : (
        <>
          <Alert>{error ?? act.error}</Alert>
          {created && (
            <Card title={`New key: ${created.name}`} className="card-highlight">
              <SecretBox value={created.key} warning="Copy this key now. For security it won't be shown again." />
              <div className="btn-row right">
                <button className="btn" onClick={() => setCreated(null)}>
                  Done
                </button>
              </div>
            </Card>
          )}
          <Card
            title="Keys"
            actions={
              <form className="inline-form" onSubmit={create}>
                <input placeholder="Key name, e.g. Production" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
                <button className="btn btn-primary" disabled={!name.trim() || act.busy === 'create'}>
                  Create key
                </button>
              </form>
            }
            className="card-flush"
          >
            {loading && !data ? (
              <Spinner />
            ) : keys.length === 0 ? (
              <Empty>No API keys yet.</Empty>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Last used</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((k) => (
                      <tr key={k.id} className={k.revoked_at ? 'is-dim' : ''}>
                        <td className="strong">{k.name}</td>
                        <td className="mono nowrap">{k.prefix}…</td>
                        <td>{k.revoked_at ? <StatusBadge status="failed" label="revoked" /> : <StatusBadge status="active" />}</td>
                        <td className="nowrap muted">{fmtRelative(k.created_at)}</td>
                        <td className="nowrap muted">{fmtRelative(k.last_used_at)}</td>
                        <td className="right">
                          {!k.revoked_at && (
                            <button className="btn btn-sm btn-danger-ghost" disabled={act.busy === `revoke-${k.id}`} onClick={() => revoke(k)}>
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
      <Integration sampleKey={created?.key ?? 'YOUR_API_KEY'} />
    </>
  );
}
